<template>
  <div class="payment-gate-container mx-auto d-flex flex-column align-center">
    <div
      v-if="showMethodSelection"
      class="d-flex tabs f-column align-center"
    >
      <PaymentGateTabs
        v-model:gate="gate"
        :selected-plan="selectedPlan"
        @switch-to-yearly="switchToYearly"
      />
    </div>
    <div
      :class="{
        'payment-gates-container-selection': multiplePaymentMethods,
      }"
      class="payment-gates-container"
    >
      <component
        :is="currentGatewayComponent"
        v-if="currentGatewayComponent"
        v-bind="gatewayProps"
        style="max-width: 100%; min-width: 0"
        @paymentDone="paymentDone = true"
      >
        <template
          v-if="showSubscriptionText"
          #text="v"
        >
          <SubscriptionText
            :tier-plan="v.tierPlan"
            :recurring="gate !== 'other'"
            :terms-text="termsText"
            :terms-url="links_url_links.gto_wizard_terms.url"
          />
        </template>
      </component>
    </div>
  </div>
</template>

<script>
import PaymentGateTabs from '@/components/Payments/PaymentGateTabs'
import SubscriptionText from '@/components/Payments/SubscriptionText'
import { createPaymentGateway } from '@/components/Payments/utils/create-payment-gateway'
import payment_mix from '@/mixins/payment_mix'
import links_mix from '@/mixins/links_mix'

export default {
  name: 'PaymentGates',
  components: {
    PaymentGateTabs,
    SubscriptionText,
  },
  mixins: [payment_mix, links_mix],
  props: {
    stripeSafeBadges: { type: Boolean, default: true },
    startGate: { type: String, default: 'card' },
    onSuccessPayment: {
      /**
       * @param {Object} params
       * @param {string} params.payment_provider
       * @param {string} params.payment_method
       */
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      paymentDone: false,
      cardMouseOver: false,
    }
  },
  beforeUnmount() {
    // Remove gate from query when component is destroyed
    const currentQuery = { ...this.$route.query }
    delete currentQuery.gate
    this.$router.replace({ query: currentQuery })
  },
  computed: {
    gate: {
      get() {
        if (this.allowedPaymentMethods.includes(this.$route.query.gate)) {
          return this.$route.query.gate
        }

        if (!this.cardEnabled) {
          return 'crypto'
        }

        return this.startGate || 'card'
      },
      set(gate) {
        this.$router.replace({
          query: { ...this.$route.query, gate },
        })
      },
    },
    showMethodSelection() {
      return this.multiplePaymentMethods && !this.paymentDone
    },
    currentGatewayComponent() {
      return createPaymentGateway(this.gate, {
        adyenEnabled: this.adyenEnabled,
        cryptoPaymentEnabled: this.cryptoPaymentEnabled,
        alipayEnabled: this.alipayEnabled,
        wechatPayEnabled: this.wechatPayEnabled,
        paypalEnabled: this.paypalEnabled,
      })
    },
    gatewayProps() {
      return {
        safeBadges: this.stripeSafeBadges ?? true,
        adyenEnabled: this.adyenEnabled ?? false,
        cryptoPaymentEnabled: this.cryptoPaymentEnabled ?? false,
        alipayEnabled: this.alipayEnabled ?? false,
        wechatPayEnabled: this.wechatPayEnabled ?? false,
        paypalEnabled: this.paypalEnabled ?? false,
        onSuccessPayment: this.onSuccessPayment,
      }
    },
    showSubscriptionText() {
      return this.gate === 'card' || this.gate === 'paypal' || this.gate === 'other'
    },
    termsText() {
      switch (this.gate) {
        case 'paypal':
          return this.$t('payment.by_clicking_paypal_btn')
        case 'other':
          return this.$t('payment.by_clicking_continue_btn')
        default:
          return this.$t('payment.by_clicking_stripe_btn')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@media (max-width: 767px) {
  .chosen-plan {
    width: 100% !important;
  }
}
.payment-gate-btns {
  width: 40rem;
  margin: auto;
  max-width: calc(100vw - 2rem);

  > div:last-child {
    &[data-state='active'],
    &:hover {
      &::before {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        background: rgba(255, 255, 255, 0.125);
        clip-path: path('M0,0 H16 V16 Q16,0 0,0 Z');
        bottom: -15.75px;
        right: -0.25px;
        pointer-events: none;
      }
    }
  }

  > div:first-child {
    &[data-state='active'],
    &:hover {
      &::before {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        background: rgba(255, 255, 255, 0.125);
        clip-path: path('M16,0 H0 V16 Q0,0 16,0 Z');
        bottom: -15.75px;
        left: -0.25px;
      }
    }
  }
}
.payment-gate-container {
  width: 44rem;
  margin: auto;
  max-width: calc(100vw - 2rem);
}
.payment-gates-container {
  width: 40rem;
  max-width: calc(100vw - 2rem);
  border-radius: 1rem;
  transition: border-radius 0.3s ease;
}
.payment-gates-container-selection {
  background: rgba(255, 255, 255, 0.125);
  padding: 2rem;
  width: 40rem;
}
.payment-gate-btn-wrapper {
  position: relative;
  flex: 1;
}

//Fix border-left radius on hover and active state
.tabs:has(.tab-wrapper:first-child:hover) + .payment-gates-container,
.tabs:has(.tab-wrapper:first-child[data-state='active']) + .payment-gates-container {
  border-top-left-radius: 0;
}

//Fix border-right radius on hover and active state
.tabs:has(.tab-wrapper:last-child:hover) + .payment-gates-container,
.tabs:has(.tab-wrapper:last-child[data-state='active']) + .payment-gates-container {
  border-top-right-radius: 0;
}

.payment-gate-btn {
  padding: 1.25rem 0;
  text-transform: uppercase;
  justify-content: center;
  border-radius: 0.75rem 0.75rem 0 0;
  --surface-bg-hover: rgba(255, 255, 255, 0.025);
  --surface-bg: transparent;
  &[data-state='active'] {
    --surface-bg-opacity: 1;
    --surface-bg-opacity-hover: 1;
    --surface-bg: rgba(255, 255, 255, 0.125);
    --surface-bg-hover: rgba(255, 255, 255, 0.125);
    pointer-events: none;
    &::after {
      background: transparent;
    }
  }

  &[data-state='disabled'] {
    pointer-events: none;
    --surface-bg-opacity: 0;
    --surface-bg-opacity-hover: 0;
    --text-clr: var(--clr-grey-1);
  }
}
.gate-box {
  border-radius: 4px;
  width: 100%;
  max-width: calc(100vw - 2rem);
}
.chosen-plan {
  position: relative;
  text-align: center;
  width: 34rem;
  font-size: 1.5rem;
  font-weight: 500;
  border-bottom: solid var(--clr-black-3) 1px;
  padding-bottom: 2rem;
  width: 80%;
  color: #d1d1d1;
  &_title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--clr-white-0);
    .month {
      font-size: 1.5rem;
      font-weight: 500;
    }
  }
  &_subtitle {
    font-size: 1rem;
    padding: 0.05rem 0 0 0;
  }
}
@media (max-width: 600px) {
  .payment-gate-container {
    max-width: calc(100vw - 4rem);
  }
}
</style>
